import React, { useRef } from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import { CloudUpload, Phone, Email } from '@mui/icons-material';

const ContactUsBlock = ({ data, isSelected, onUpdate, previewMode, blockId, onSelectBlock }) => {
  const fileInputRefs = useRef({});
  
  const {
    title = 'Contact Us',
    contacts = [
      {
        id: '1',
        avatar: '',
        fullName: 'John Doe',
        position: 'CEO',
        description: 'Feel free to reach out for any business inquiries.',
        phone: '+1 (555) 123-4567',
        email: 'john@company.com'
      }
    ],
    // Стили
    backgroundColor = '#ffffff',
    titleColor = '#333333',
    nameColor = '#333333',
    positionColor = '#666666',
    descriptionColor = '#666666',
    contactColor = '#007bff',
    borderColor = '#e0e0e0',
    titleFontSize = 28,
    nameFontSize = 18,
    positionFontSize = 14,
    descriptionFontSize = 14,
    contactFontSize = 14,
    // Layout
    alignment = 'center',
    cardsPerRow = 2,
    cardSpacing = 20,
    padding = 30,
    marginTop = 20,
    marginBottom = 20,
    borderRadius = 8,
    fontFamily = 'Arial, sans-serif',
  } = data;

  const handleFileUpload = (contactId, event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Увеличиваем лимит до 10MB для высококачественных изображений
      if (file.size > 10 * 1024 * 1024) {
        alert('File size should be less than 10MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        // Используем изображение БЕЗ СЖАТИЯ для максимального качества
        const originalDataUrl = e.target.result;
        
        const updatedContacts = contacts.map(contact => 
          contact.id === contactId 
            ? { ...contact, avatar: originalDataUrl }
            : contact
        );
        
        if (onUpdate) {
          // Используем setTimeout для предотвращения блокировки UI
          setTimeout(() => {
            onUpdate({ contacts: updatedContacts });
          }, 0);
        }
      };
      
      reader.onerror = () => {
        alert('Failed to read file. Please try again.');
      };
      
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  const getResponsiveCardsPerRow = () => {
    switch (previewMode) {
      case 'mobile':
        return 1;
      case 'tablet':
        return Math.min(cardsPerRow, 2);
      default:
        return cardsPerRow;
    }
  };

  const containerStyles = {
    padding: `${padding}px`,
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    backgroundColor,
    border: isSelected ? '2px dashed #007bff' : 'none',
    borderRadius: isSelected ? '4px' : `${borderRadius}px`,
    textAlign: alignment,
  };

  const titleStyles = {
    fontSize: `${titleFontSize}px`,
    fontWeight: 'bold',
    color: titleColor,
    marginBottom: '30px',
    fontFamily,
  };

  const cardsContainerStyles = {
    display: 'grid',
    gridTemplateColumns: `repeat(${getResponsiveCardsPerRow()}, 1fr)`,
    gap: `${cardSpacing}px`,
    maxWidth: '1200px',
    margin: '0 auto',
  };

  const cardStyles = {
    backgroundColor: '#ffffff',
    border: `1px solid ${borderColor}`,
    borderRadius: `${borderRadius}px`,
    padding: '20px',
    textAlign: alignment,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  };

  const renderContactCard = (contact) => {
    return (
      <Box key={contact.id} sx={cardStyles}>
        {/* Avatar Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: alignment === 'center' ? 'center' : alignment === 'right' ? 'flex-end' : 'flex-start', 
          mb: 2 
        }}>
          {contact.avatar ? (
            <Avatar
              src={contact.avatar}
              alt={contact.fullName}
              sx={{
                width: 80,
                height: 80,
                backgroundColor: '#e0e0e0',
                cursor: 'pointer',
              }}
              onClick={() => fileInputRefs.current[contact.id]?.click()}
            />
          ) : (
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#e0e0e0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#d0d0d0',
                },
              }}
              onClick={() => fileInputRefs.current[contact.id]?.click()}
            >
              <CloudUpload sx={{ color: '#666', fontSize: 24 }} />
            </Box>
          )}
        </Box>

        {/* Contact Info */}
        <Typography
          sx={{
            fontSize: `${nameFontSize}px`,
            fontWeight: 'bold',
            color: nameColor,
            mb: 1,
            fontFamily,
          }}
        >
          {contact.fullName}
        </Typography>

        <Typography
          sx={{
            fontSize: `${positionFontSize}px`,
            color: positionColor,
            mb: 2,
            fontWeight: '500',
            fontFamily,
          }}
        >
          {contact.position}
        </Typography>

        {contact.description && (
          <Typography
            sx={{
              fontSize: `${descriptionFontSize}px`,
              color: descriptionColor,
              mb: 2,
              lineHeight: 1.5,
              fontFamily,
            }}
          >
            {contact.description}
          </Typography>
        )}

        {/* Contact Details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {contact.phone && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: alignment === 'center' ? 'center' : alignment === 'right' ? 'flex-end' : 'flex-start', 
              gap: 1 
            }}>
              <Phone sx={{ fontSize: 16, color: contactColor }} />
              <Typography
                sx={{
                  fontSize: `${contactFontSize}px`,
                  color: contactColor,
                  fontFamily,
                }}
              >
                {contact.phone}
              </Typography>
            </Box>
          )}

          {contact.email && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: alignment === 'center' ? 'center' : alignment === 'right' ? 'flex-end' : 'flex-start', 
              gap: 1 
            }}>
              <Email sx={{ fontSize: 16, color: contactColor }} />
              <Typography
                sx={{
                  fontSize: `${contactFontSize}px`,
                  color: contactColor,
                  fontFamily,
                }}
              >
                {contact.email}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Hidden file input */}
        <input
          type="file"
          ref={(el) => fileInputRefs.current[contact.id] = el}
          onChange={(e) => handleFileUpload(contact.id, e)}
          accept="image/*"
          style={{ display: 'none' }}
        />
      </Box>
    );
  };

  return (
    <Box 
      sx={containerStyles}
      onClick={(e) => {
        e.stopPropagation();
        if (onSelectBlock) {
          onSelectBlock({ id: blockId, type: 'contactUs', data });
        }
      }}
    >
      {title && (
        <Typography sx={titleStyles}>
          {title}
        </Typography>
      )}

      <Box sx={cardsContainerStyles}>
        {contacts.map(contact => renderContactCard(contact))}
      </Box>
    </Box>
  );
};

export default ContactUsBlock; 